import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { 
  Loader2, 
  BarChart,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  Lightbulb,
  Download,
} from "lucide-react";
import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { HeaderSection } from "./market-report/HeaderSection";
import { MarketMetrics } from "./market-report/MarketMetrics";
import { UserInfoForm } from "./market-report/UserInfoForm";
import { ReportSection } from "./market-report/ReportSection";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface MarketSizeReportProps {
  tam: number;
  sam: number;
  som: number;
  currency: string;
  country: string;
  businessArea: string;
  sector: string;
  marketSegment: string;
  targetCustomerType: string;
  potentialCustomers: number;
  revenuePerCustomer: number;
  marketAccessibility: number;
  accessibilityLimitations: string[];
  marketPenetration: number;
  customerAcquisitionRate: number;
  penetrationStrategies: string[];
  marketValidation: string;
  benchmarkRequested: boolean;
  churnRate: number;
  customerLTV: number;
  customerCAC: number;
  tamCustomers?: number;
  samCustomers?: number;
  somCustomers?: number;
  children: [React.ReactNode, React.ReactNode, React.ReactNode, React.ReactNode, React.ReactNode];
}

export const MarketSizeReport = ({ 
  tam, 
  sam, 
  som, 
  currency,
  country,
  businessArea,
  sector,
  marketSegment,
  targetCustomerType,
  potentialCustomers,
  revenuePerCustomer,
  marketAccessibility,
  accessibilityLimitations,
  marketPenetration,
  customerAcquisitionRate,
  penetrationStrategies,
  marketValidation,
  benchmarkRequested,
  churnRate,
  customerLTV,
  customerCAC,
  tamCustomers = 0,
  samCustomers = 0,
  somCustomers = 0,
  children
}: MarketSizeReportProps) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<string>("");
  const { toast } = useToast();
  const [sizeMetric, setSizeMetric] = useState<"money" | "customers" | "both">("money");
  const reportRef = useRef<HTMLDivElement>(null);

  const parseMarkdown = (text: string): string => {
    const cleanText = text
      .replace(/^[#]+\s*(.*?)\s*$/gm, '$1')
      .replace(/^\d+\.\s+/gm, '')
      .trim();

    return cleanText
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 hover:underline">$1</a>');
  };

  const parseReportSections = (report: string) => {
    if (!report) return [];
    
    const paragraphs = report.split('\n\n').filter(p => p.trim());
    
    return paragraphs.map((p, index) => {
      const content = p.trim();
      
      if (index === 0) {
        return {
          title: 'Market Overview',
          content: [parseMarkdown(content)],
          icon: <Globe className="h-5 w-5 text-purple-600" />,
          type: 'overview' as const
        };
      }
      
      const points = content
        .split(/(?:\r?\n|\r)/)
        .map(line => parseMarkdown(line.trim()))
        .filter(line => line.length > 0);
      
      if (content.toLowerCase().includes('opportunity') || content.toLowerCase().includes('growth')) {
        return {
          title: 'Market Opportunity',
          content: points,
          icon: <ArrowUpRight className="h-5 w-5 text-green-600" />,
          type: 'success' as const
        };
      } else if (content.toLowerCase().includes('challenge') || content.toLowerCase().includes('risk')) {
        return {
          title: 'Market Challenges',
          content: points,
          icon: <ArrowDownRight className="h-5 w-5 text-amber-600" />,
          type: 'warning' as const
        };
      } else if (content.toLowerCase().includes('strategy') || content.toLowerCase().includes('recommend')) {
        return {
          title: 'Strategic Insights',
          content: points,
          icon: <Lightbulb className="h-5 w-5 text-blue-600" />,
          type: 'info' as const
        };
      } else {
        return {
          title: 'Additional Insights',
          content: points,
          icon: <BarChart className="h-5 w-5 text-blue-600" />,
          type: 'info' as const
        };
      }
    });
  };

  const generateReport = async () => {
    if (!tam || !sam || !som || !currency || !country || !businessArea || !sector) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: reportData, error: reportError } = await supabase.functions.invoke('generate-market-report', {
        body: {
          tam,
          sam,
          som,
          currency,
          country,
          businessArea,
          sector,
          name: "Anonymous User",
          startupName: "Market Analysis",
          email: "anonymous@example.com",
          newsletter: false,
          contactConsent: false,
          marketSegment,
          targetCustomerType,
          potentialCustomers,
          revenuePerCustomer,
          marketAccessibility,
          accessibilityLimitations,
          marketPenetration,
          customerAcquisitionRate,
          penetrationStrategies,
          marketValidation,
          benchmarkRequested,
          churnRate,
          customerLTV,
          customerCAC
        }
      });

      if (reportError) throw reportError;

      const { error: storageError } = await supabase
        .from('market_report_tool')
        .insert({
          name: "Anonymous User",
          startup_name: "Market Analysis",
          email: "anonymous@example.com",
          newsletter: false,
          contact_consent: false,
          tam,
          sam,
          som,
          currency,
          country,
          business_area: businessArea,
          sector,
          report_content: reportData.report,
          market_segment: marketSegment,
          target_customer_type: targetCustomerType,
          potential_customers: potentialCustomers,
          revenue_per_customer: revenuePerCustomer,
          market_accessibility_percentage: marketAccessibility,
          accessibility_limitations: accessibilityLimitations,
          market_penetration_percentage: marketPenetration,
          customer_acquisition_rate: customerAcquisitionRate,
          penetration_strategies: penetrationStrategies,
          market_validation: marketValidation,
          benchmark_requested: benchmarkRequested,
          churn_rate: churnRate,
          customer_ltv: customerLTV,
          customer_cac: customerCAC
        });

      if (storageError) throw storageError;

      setReport(reportData.report);
      
      toast({
        title: "Success",
        description: "Your market analysis report has been generated.",
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to generate report. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!reportRef.current) return;

    try {
      setLoading(true);
      toast({
        title: "Generating PDF",
        description: "Please wait while we generate your report...",
      });

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, imgHeight);

      // Add additional pages if content exceeds page height
      if (imgHeight > 297) { // A4 height in mm
        let remainingHeight = imgHeight;
        let currentPosition = -297;
        
        while (remainingHeight > 297) {
          pdf.addPage();
          currentPosition -= 297;
          pdf.addImage(
            canvas.toDataURL('image/png'),
            'PNG',
            0,
            currentPosition,
            imgWidth,
            imgHeight
          );
          remainingHeight -= 297;
        }
      }

      pdf.save(`market-report.pdf`);

      toast({
        title: "Success",
        description: "Your report has been downloaded successfully.",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const sections = parseReportSections(report);

  return (
    <div className="space-y-6">
      <HeaderSection />
      
      {/* Top Row - 2 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Info Box - Left */}
        <Card className="p-6 bg-white">
          <h3 className="text-xl font-semibold text-[#2F3039] mb-4">Market Analysis Tool</h3>
          <UserInfoForm />
        </Card>

        {/* Chart Box - Right */}
        <Card className="p-6 bg-white">
          <h3 className="text-xl font-semibold text-[#2F3039] mb-4">Market Size Visualization</h3>
          <MarketMetrics
            tam={tam}
            sam={sam}
            som={som}
            currency={currency}
            tamCustomers={tamCustomers}
            samCustomers={samCustomers}
            somCustomers={somCustomers}
            sizeMetric={sizeMetric}
          />
        </Card>
      </div>

      {/* Market Size Box - Full Width */}
      <Card className="p-6 bg-white">
        <h3 className="text-xl font-semibold text-[#2F3039] mb-4">Market Size</h3>
        <div className="mb-6">
          <RadioGroup
            value={sizeMetric}
            onValueChange={(value) => setSizeMetric(value as "money" | "customers" | "both")}
            className="flex space-x-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="money" id="money" />
              <Label htmlFor="money">Money</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="customers" id="customers" />
              <Label htmlFor="customers">Customers</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="both" id="both" />
              <Label htmlFor="both">Both</Label>
            </div>
          </RadioGroup>
        </div>
        {Array.isArray(children) && children[0]}
      </Card>

      {/* Market Parameters Grid - 2x2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6 bg-white">
          <h3 className="text-xl font-semibold text-[#2F3039] mb-4">Currency & Region</h3>
          <div className="space-y-4">
            {Array.isArray(children) && children[1]}
          </div>
        </Card>

        <Card className="p-6 bg-white">
          <h3 className="text-xl font-semibold text-[#2F3039] mb-4">Business Details</h3>
          <div className="space-y-4">
            {Array.isArray(children) && children[2]}
          </div>
        </Card>

        <Card className="p-6 bg-white">
          <h3 className="text-xl font-semibold text-[#2F3039] mb-4">Market Analysis</h3>
          <div className="space-y-4">
            {Array.isArray(children) && children[3]}
          </div>
        </Card>

        <Card className="p-6 bg-white">
          <h3 className="text-xl font-semibold text-[#2F3039] mb-4">Additional Parameters</h3>
          <div className="space-y-4">
            {Array.isArray(children) && children[4]}
          </div>
        </Card>
      </div>

      {/* Centered Generate Report Button */}
      <div className="flex justify-center mt-8 gap-4">
        <Button
          onClick={generateReport}
          disabled={loading || !tam || !sam || !som || !currency || !country || !businessArea || !sector}
          className="bg-[#E72A4E] hover:bg-[#E72A4E]/90 text-white px-8"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Analyzing Market...
            </>
          ) : (
            <>
              <BarChart className="mr-2 h-4 w-4" />
              Generate Report
            </>
          )}
        </Button>
        {report && (
          <Button
            onClick={downloadPDF}
            disabled={loading}
            variant="outline"
            className="px-8"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Save as PDF
              </>
            )}
          </Button>
        )}
      </div>

      {/* Report Section - Full Width */}
      {report && (
        <Card className="p-6 bg-white" ref={reportRef}>
          <h4 className="text-lg font-semibold text-[#2F3039] mb-4">Market Analysis Report</h4>
          <div className="grid grid-cols-1 gap-4">
            {sections.map((section, index) => (
              <ReportSection
                key={index}
                title={section.title}
                content={section.content}
                icon={section.icon}
                type={section.type}
              />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
